import React, { useState } from 'react';
import {
  CheckCircle, Warning, ShieldWarning, XCircle, ClockCountdown,
  Funnel, FileText, FilePdf
} from '@phosphor-icons/react';
import { getCbamDeclarationPack } from '../api';
import { exportCbamDeclarationPack } from '../utils/exportCbamDeclaration';

const STATUS_STYLE = {
  'Actual Verified': { cls: 'text-emerald-400 bg-emerald-500/10 border-emerald-500/30', icon: CheckCircle },
  'Actual Unverified': { cls: 'text-amber-400 bg-amber-500/10 border-amber-500/30', icon: Warning },
  'Supplier-Declared': { cls: 'text-blue-400 bg-blue-500/10 border-blue-500/30', icon: FileText },
  'Pending Review': { cls: 'text-orange-400 bg-orange-500/10 border-orange-500/30', icon: ClockCountdown },
  'Default Applied': { cls: 'text-red-400 bg-red-500/10 border-red-500/30', icon: ShieldWarning },
  'Non-Compliant': { cls: 'text-red-600 bg-red-600/10 border-red-600/30', icon: XCircle },
};

export default function CbamShipments({ data }) {
  const [statusFilter, setStatusFilter] = useState('all');
  const [periodFilter, setPeriodFilter] = useState('all');

  if (!data) return null;
  const { shipments, declarations, installations } = data;

  const periods = [...new Set((shipments || []).map(s => s.declaration_period))];
  const statuses = Object.keys(STATUS_STYLE);

  const filtered = (shipments || []).filter(s => {
    if (statusFilter !== 'all' && s.status !== statusFilter) return false;
    if (periodFilter !== 'all' && s.declaration_period !== periodFilter) return false;
    return true;
  });

  return (
    <div className="space-y-5 animate-fade-in" data-testid="cbam-shipments">
      {/* Declaration Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {(declarations || []).map((d, i) => (
          <div key={i} className="bg-surface border border-border rounded-lg p-5" data-testid={`cbam-decl-${d.period.replace(/\s+/g, '-')}`}>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-base font-body font-semibold text-text-primary">{d.period} Declaration</h3>
              <span className={`text-[10px] font-semibold px-2.5 py-1 rounded-full ${d.status === 'In Preparation' ? 'bg-amber-500/10 text-amber-400' : 'bg-blue-500/10 text-blue-400'}`}>{d.status}</span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs font-body mb-4">
              <div><span className="text-text-secondary">Total Shipments</span><p className="font-semibold text-text-primary text-lg">{d.total_shipments}</p></div>
              <div><span className="text-text-secondary">Verified</span><p className="font-semibold text-emerald-400 text-lg">{d.verified_shipments}</p></div>
              <div><span className="text-text-secondary">Default/Pending</span><p className="font-semibold text-amber-400 text-lg">{d.default_shipments + d.pending_shipments}</p></div>
              <div><span className="text-text-secondary">Net Liability</span><p className="font-semibold text-text-primary text-lg">EUR {d.net_liability_eur?.toLocaleString()}</p></div>
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between text-xs font-body">
                <span className="text-text-secondary">Declaration Readiness</span>
                <span className="font-semibold text-text-primary">{d.declaration_readiness_pct}%</span>
              </div>
              <div className="w-full bg-white/5 rounded-full h-2.5">
                <div className={`h-2.5 rounded-full transition-all ${d.declaration_readiness_pct >= 70 ? 'bg-emerald-500' : d.declaration_readiness_pct >= 40 ? 'bg-amber-500' : 'bg-red-500'}`} style={{ width: `${d.declaration_readiness_pct}%` }} />
              </div>
              <div className="flex items-center justify-between text-[11px] font-body text-text-secondary">
                <span>Deadline: {d.deadline}</span>
                <span>Embedded: {d.total_embedded_tco2e?.toLocaleString()} tCO2e</span>
              </div>
              <button
                data-testid={`cbam-export-decl-${d.period.replace(/\s+/g, '-')}`}
                onClick={() => exportCbamDeclarationPack(getCbamDeclarationPack, d.period.replace(/\s+/g, '-'))}
                className="w-full mt-3 flex items-center justify-center gap-2 py-2 bg-primary/10 border border-primary/30 text-primary rounded-lg text-xs font-body font-semibold hover:bg-primary/20 transition-colors"
              >
                <FilePdf size={16} weight="duotone" />
                Export Declaration Pack (PDF)
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-center">
        <div className="flex items-center gap-1.5 text-xs font-body text-text-secondary">
          <Funnel size={14} />
          <span>Filter Shipments:</span>
        </div>
        <select
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value)}
          className="bg-surface border border-border rounded-md px-3 py-1.5 text-xs font-body text-text-primary focus:outline-none focus:border-primary"
          data-testid="cbam-shipment-status-filter"
        >
          <option value="all">All Statuses</option>
          {statuses.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <select
          value={periodFilter}
          onChange={e => setPeriodFilter(e.target.value)}
          className="bg-surface border border-border rounded-md px-3 py-1.5 text-xs font-body text-text-primary focus:outline-none focus:border-primary"
          data-testid="cbam-shipment-period-filter"
        >
          <option value="all">All Periods</option>
          {periods.map(p => <option key={p} value={p}>{p}</option>)}
        </select>
        <span className="text-xs font-body text-text-secondary ml-auto">{filtered.length} shipment{filtered.length !== 1 ? 's' : ''}</span>
      </div>

      {/* Shipment Table */}
      <div className="bg-surface border border-border rounded-lg p-5" data-testid="cbam-shipment-table">
        <div className="overflow-x-auto">
          <table className="w-full text-xs font-body">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left py-2 text-text-secondary font-semibold">Shipment</th>
                <th className="text-left py-2 text-text-secondary font-semibold">Product</th>
                <th className="text-right py-2 text-text-secondary font-semibold">Qty (t)</th>
                <th className="text-left py-2 text-text-secondary font-semibold">Destination</th>
                <th className="text-left py-2 text-text-secondary font-semibold">Importer</th>
                <th className="text-right py-2 text-text-secondary font-semibold">Embedded</th>
                <th className="text-right py-2 text-text-secondary font-semibold">Net CBAM</th>
                <th className="text-right py-2 text-text-secondary font-semibold">Evidence</th>
                <th className="text-center py-2 text-text-secondary font-semibold">Status</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((s, i) => {
                const sty = STATUS_STYLE[s.status] || STATUS_STYLE['Pending Review'];
                const SIcon = sty.icon;
                return (
                  <tr key={i} className="border-b border-border/50 hover:bg-white/[0.02]" data-testid={`cbam-ship-${s.shipment_id}`}>
                    <td className="py-2.5">
                      <span className="text-text-primary font-medium font-mono">{s.shipment_id}</span>
                      <p className="text-[10px] text-text-secondary">{s.ship_date}</p>
                    </td>
                    <td className="py-2.5 text-text-secondary">{s.product_desc}</td>
                    <td className="py-2.5 text-right text-text-primary font-semibold">{s.quantity_tonnes}</td>
                    <td className="py-2.5 text-text-secondary">{s.destination_country}</td>
                    <td className="py-2.5 text-text-secondary max-w-[140px] truncate">{s.importer}</td>
                    <td className="py-2.5 text-right text-text-secondary">{s.embedded_total_tco2e} tCO2e</td>
                    <td className="py-2.5 text-right font-semibold text-amber-400">EUR {s.net_cbam_eur?.toLocaleString()}</td>
                    <td className="py-2.5 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <div className="w-16 bg-white/5 rounded-full h-1.5">
                          <div className={`h-1.5 rounded-full ${s.evidence_completeness_pct >= 80 ? 'bg-emerald-500' : s.evidence_completeness_pct >= 50 ? 'bg-amber-500' : 'bg-red-500'}`} style={{ width: `${s.evidence_completeness_pct}%` }} />
                        </div>
                        <span className="text-[10px] text-text-secondary w-8 text-right">{s.evidence_completeness_pct}%</span>
                      </div>
                    </td>
                    <td className="py-2.5 text-center">
                      <span className={`inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full border ${sty.cls}`}>
                        <SIcon size={10} weight="fill" />
                        {s.status}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Installation Summary */}
      <div className="bg-surface border border-border rounded-lg p-5" data-testid="cbam-installations">
        <h3 className="text-sm font-body font-semibold text-text-primary mb-4">Installation Summary</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {(installations || []).map((inst, i) => {
            const verified = inst.verification_status === 'Verified';
            return (
              <div key={i} className="border border-border rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-body font-semibold text-text-primary">{inst.site_name}</span>
                  {verified ? (
                    <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-emerald-400 bg-emerald-500/10 px-2 py-0.5 rounded-full"><CheckCircle size={10} weight="fill" />Verified</span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-amber-400 bg-amber-500/10 px-2 py-0.5 rounded-full"><Warning size={10} weight="fill" />Pending</span>
                  )}
                </div>
                <div className="space-y-1 text-xs font-body text-text-secondary">
                  <p>{inst.country} ({inst.country_code}) — {inst.operator_entity}</p>
                  <p>Direct: {inst.direct_emissions_tco2e?.toLocaleString()} tCO2e — Indirect: {inst.indirect_emissions_tco2e?.toLocaleString()} tCO2e</p>
                  <p>Grid: {inst.grid_factor_tco2e_mwh} tCO2e/MWh — Verifier: {inst.verifier}</p>
                  <div className="flex flex-wrap gap-1 mt-2">
                    {(inst.production_routes || []).map((r, ri) => (
                      <span key={ri} className="text-[10px] bg-white/5 border border-border px-1.5 py-0.5 rounded">{r}</span>
                    ))}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
